import {
  Checkbox,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import Iconify from 'src/components/Iconify';
import TableHeadTypography from 'src/components/TableHeadTypography';
import TableContainer from 'src/components/Table/TableContainer';
import LoadingRows from 'src/components/Table/LoadingRows';
import NoDataCell from 'src/components/NoDataCell';
import HighlightedText from 'src/components/HighlightedText';
import UnsignedChip from 'src/components/UnsignedChip';
import useTypographyMediaQuery from 'src/hooks/useTypographyMediaQuery';
import { formatCount } from 'src/utils/formatNumber.util';
import {
  getCustomerEnvironmentApplicationStatusColor,
  getCustomerEnvironmentApplicationStatusLabel,
} from 'src/utils/enums/customerEnvironmentApplicationStatus';

// No sort column means the server's default, by path.
export const DEFAULT_FILE_SORT = { column: '', direction: 'asc' };

export const toggleFileSort = (previous, sortKey) =>
  previous.column === sortKey
    ? { column: sortKey, direction: previous.direction === 'desc' ? 'asc' : 'desc' }
    : { column: sortKey, direction: 'asc' };

const SELECT_COLUMN = { key: 'select', label: '', width: '44px' };

// Widths are weights shared out among whichever columns are showing, so hiding one widens the rest.
export const WIN32_FILE_COLUMNS = [
  { key: 'fileName', label: 'File Name', weight: 2.6, sortKey: 'fileName', required: true },
  { key: 'authenticodeHash', label: 'Authenticode Hash', weight: 2.2 },
  { key: 'productName', label: 'Product Name', weight: 1.5, sortKey: 'productName' },
  { key: 'fileVersion', label: 'Version', weight: 0.9 },
  { key: 'filePath', label: 'Full File Path', weight: 2.8 },
  { key: 'signers', label: 'Signers', weight: 1.6 },
  { key: 'isCovered', label: 'Covered', weight: 0.8 },
];

const HIDDEN_BY_DEFAULT_WIN32_FILE_COLUMNS = ['authenticodeHash', 'isCovered'];

export const DEFAULT_VISIBLE_WIN32_FILE_COLUMNS = WIN32_FILE_COLUMNS.filter(
  column => !HIDDEN_BY_DEFAULT_WIN32_FILE_COLUMNS.includes(column.key),
).map(column => column.key);

// A file is signed when any signer was recorded for it, which is what the Signers column lists.
export const SIGNED_FILTER_OPTION = {
  key: 'isSigned',
  type: 'checkbox',
  label: 'Signature',
  dataType: 'boolean',
  options: [
    { value: true, label: 'Signed' },
    { value: false, label: 'Not signed' },
  ],
};

// Checking both boxes, like checking neither, shows every file, so only a single box narrows.
export const toIsSigned = values => (values?.length === 1 ? values[0] : null);

const ELLIPSIS_SX = {
  display: 'block',
  minWidth: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  lineHeight: 1.25,
};

const PATH_SX = {
  fontSize: '0.6rem',
  lineHeight: 1.25,
  display: '-webkit-box',
  WebkitLineClamp: 3,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
  wordBreak: 'break-all',
};

const SUBTEXT_SX = { fontSize: '0.6rem', color: '#9CA3AF' };

// The table a single app lists its files in, one row per file at its first path. The page picks
// which of WIN32_FILE_COLUMNS show, and one that acts on files passes a selection. A row click
// belongs to onRowClick, so a selection is made through the checkboxes alone.
const Win32FileTable = ({
  rows,
  columns: visibleColumns,
  isLoading,
  isFetchingNextPage,
  lastElementRef,
  searchValue,
  sort,
  onSort,
  selection,
  onRowClick,
  emptyText,
  maxHeight = 520,
}) => {
  const { mediumScreen, largeScreen, smallScreen } = useTypographyMediaQuery();

  const totalWeight = visibleColumns.reduce((sum, column) => sum + column.weight, 0);
  const columns = [
    ...(selection ? [SELECT_COLUMN] : []),
    ...visibleColumns.map(column => ({
      ...column,
      width: `${(column.weight / totalWeight) * 100}%`,
    })),
  ];
  const canSelect = !!selection?.enabled;

  // Select all takes the rows loaded so far, since those are the ones on screen to be checked.
  const selectedLoadedCount = selection
    ? rows.filter(row => selection.selected.has(row.fileId)).length
    : 0;
  const allLoadedSelected = rows.length > 0 && selectedLoadedCount === rows.length;

  const renderCell = (column, row) => {
    switch (column.key) {
      case 'select':
        return (
          <TableCell key="select" sx={{ py: 0.25, px: 0.5 }}>
            <Checkbox
              size="small"
              disabled={!canSelect}
              checked={selection.selected.has(row.fileId)}
              onChange={() => selection.onToggle(row.fileId)}
              onClick={event => event.stopPropagation()}
              sx={{
                '& .MuiSvgIcon-root path': { fill: canSelect ? '#7DFF92' : '#6B7280' },
              }}
            />
          </TableCell>
        );
      // The name and its hash share a cell, as on the unmatched files list, each cut to one line.
      case 'fileName':
        return (
          <TableCell key="fileName" sx={{ py: 0.5, minWidth: 0 }}>
            <Stack gap="2px" sx={{ minWidth: 0 }}>
              <Stack direction="row" gap={0.75} alignItems="center" sx={{ minWidth: 0 }}>
                <Typography
                  variant="caption"
                  title={row.fileName}
                  sx={{ ...ELLIPSIS_SX, fontSize: '0.7rem' }}>
                  <HighlightedText text={row.fileName} searchValue={searchValue} fallback="—" />
                </Typography>
                {row.signerCount === 0 && <UnsignedChip />}
              </Stack>
              <Typography
                variant="caption"
                title={row.sha256}
                sx={{ ...ELLIPSIS_SX, fontSize: '0.65rem', color: '#9CA3AF' }}>
                <HighlightedText text={row.sha256} searchValue={searchValue} fallback="—" />
              </Typography>
            </Stack>
          </TableCell>
        );
      case 'authenticodeHash':
        return (
          <TableCell key="authenticodeHash" sx={{ py: 0.5, minWidth: 0 }}>
            <Typography
              variant="caption"
              title={row.authenticodeHashSha256 || undefined}
              sx={{ ...ELLIPSIS_SX, fontSize: '0.65rem', color: '#9CA3AF' }}>
              <HighlightedText
                text={row.authenticodeHashSha256}
                searchValue={searchValue}
                fallback="—"
              />
            </Typography>
          </TableCell>
        );
      case 'filePath':
        return (
          <TableCell key="filePath" sx={{ py: 0.5, minWidth: 0 }}>
            <Typography variant="caption" title={row.filePath} sx={PATH_SX}>
              <HighlightedText text={row.filePath} searchValue={searchValue} fallback="—" />
            </Typography>
            {row.pathCount > 1 && (
              <Typography variant="caption" sx={SUBTEXT_SX}>
                {`+${formatCount(row.pathCount - 1)} more ${
                  row.pathCount === 2 ? 'path' : 'paths'
                }`}
              </Typography>
            )}
          </TableCell>
        );
      // Like the path, the first signer stands for the rest with a count of them beneath.
      case 'signers':
        return (
          <TableCell key="signers" sx={{ py: 0.5, minWidth: 0 }}>
            <Typography
              variant="caption"
              title={row.signer || undefined}
              sx={{ ...ELLIPSIS_SX, fontSize: '0.7rem' }}>
              {row.signerCount > 0 ? row.signer || 'Unnamed signer' : '—'}
            </Typography>
            {row.signerCount > 1 && (
              <Typography variant="caption" sx={SUBTEXT_SX}>
                {`+${formatCount(row.signerCount - 1)} more ${
                  row.signerCount === 2 ? 'signer' : 'signers'
                }`}
              </Typography>
            )}
          </TableCell>
        );
      // A covered file says whether an enabled import holds it; any one enabled import is enough.
      case 'isCovered':
        return (
          <TableCell key="isCovered" sx={{ py: 0.5 }}>
            <Typography variant="caption" sx={{ fontSize: '0.7rem', display: 'block' }}>
              {row.isCovered ? 'Yes' : 'No'}
            </Typography>
            {row.isCovered && row.coveringStatus ? (
              <Typography
                variant="caption"
                sx={{
                  ...SUBTEXT_SX,
                  color: getCustomerEnvironmentApplicationStatusColor(row.coveringStatus),
                }}>
                {getCustomerEnvironmentApplicationStatusLabel(row.coveringStatus)}
              </Typography>
            ) : null}
          </TableCell>
        );
      default:
        return (
          <TableCell key={column.key} sx={{ py: 0.5, minWidth: 0 }}>
            <Typography
              variant="caption"
              title={row[column.key] || undefined}
              sx={{ ...ELLIPSIS_SX, fontSize: '0.7rem' }}>
              {row[column.key] || '—'}
            </Typography>
          </TableCell>
        );
    }
  };

  return (
    <TableContainer roundedTop styles={{ maxHeight }}>
      <Table
        stickyHeader
        size="small"
        sx={{
          tableLayout: 'fixed',
          width: '100%',
          '& .MuiTableCell-root': { fontSize: '0.7rem' },
        }}>
        <colgroup>
          {columns.map(column => (
            <col key={column.key} style={{ width: column.width }} />
          ))}
        </colgroup>
        <TableHead sx={{ backgroundColor: '#3f3f3f' }}>
          <TableRow>
            {columns.map(column => {
              if (column.key === 'select') {
                return (
                  <TableCell key="select" sx={{ py: 0.25, px: 0.5 }}>
                    <Checkbox
                      size="small"
                      disabled={!canSelect || rows.length === 0}
                      checked={allLoadedSelected}
                      indeterminate={selectedLoadedCount > 0 && !allLoadedSelected}
                      onChange={() =>
                        selection.onSetSelected(
                          allLoadedSelected ? [] : rows.map(row => row.fileId),
                        )
                      }
                      sx={{
                        '& .MuiSvgIcon-root path': { fill: canSelect ? '#7DFF92' : '#6B7280' },
                      }}
                    />
                  </TableCell>
                );
              }

              const isSorted = !!column.sortKey && sort.column === column.sortKey;

              return (
                <TableCell key={column.key} sx={{ p: '8px 16px', verticalAlign: 'middle' }}>
                  <Stack
                    direction="row"
                    gap={0.5}
                    alignItems="center"
                    onClick={column.sortKey ? () => onSort(column.sortKey) : undefined}
                    sx={{ cursor: column.sortKey ? 'pointer' : 'default' }}>
                    <TableHeadTypography
                      hasSorting={isSorted}
                      mediumScreen={mediumScreen}
                      largeScreen={largeScreen}
                      smallScreen={smallScreen}>
                      {column.label}
                    </TableHeadTypography>
                    {column.sortKey ? (
                      <Iconify
                        icon={
                          isSorted
                            ? sort.direction === 'asc'
                              ? 'mdi:arrow-up'
                              : 'mdi:arrow-down'
                            : 'mdi:swap-vertical'
                        }
                        width={14}
                        height={14}
                        color={isSorted ? '#7DFF92' : '#9CA3AF'}
                      />
                    ) : null}
                  </Stack>
                </TableCell>
              );
            })}
          </TableRow>
        </TableHead>
        <TableBody sx={{ backgroundColor: '#4B4B4B' }}>
          {isLoading ? (
            <LoadingRows columnCount={columns.length} rowCount={8} />
          ) : (
            <>
              {rows.map((row, index) => (
                <TableRow
                  key={row.fileId}
                  ref={index === rows.length - 1 ? lastElementRef : null}
                  hover
                  onClick={onRowClick ? () => onRowClick(row) : undefined}
                  sx={{ cursor: onRowClick ? 'pointer' : 'default' }}>
                  {columns.map(column => renderCell(column, row))}
                </TableRow>
              ))}
              {isFetchingNextPage && <LoadingRows columnCount={columns.length} rowCount={3} />}
              {!isFetchingNextPage && rows.length === 0 && (
                <NoDataCell colSpan={columns.length}>{emptyText}</NoDataCell>
              )}
            </>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default Win32FileTable;
